import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import {
  faBan, faCheckCircle,
} from '@fortawesome/pro-light-svg-icons'

import { sortArrayBy } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import type { UseFiltersPayload } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useAgencies from '@hooks/useAgencies'
import useCurrentUser, { CurrentUserPayload } from '@hooks/useCurrentUser'
import usePricingPlans from '@hooks/usePricingPlans'
import useSelector from '@hooks/useSelector'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import type { EntitiesState } from '@redux/entities'

import AgencyListItem from '../AgencyListItem'

const buildPageFilterFields = (entities: EntitiesState, currentUserPayload: CurrentUserPayload) => {
  const { pricingPlans } = entities
  const { isBrandUser } = currentUserPayload

  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: isBrandUser ? 'Filter by Title or ID' : 'Filter by Title, ID or Portal Identity Code',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      hidden: isBrandUser,
      key: 'brand_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      hidden: isBrandUser,
      key: 'marketing_platform',
      label: 'Marketing Platforms',
      values: [
        { key: 'campaigntrack', label: 'Campaigntrack' },
        { key: 'realhub', label: 'Realhub' },
        { key: 'other', label: 'Other' },
      ],
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      hidden: isBrandUser,
      key: 'contract_status',
      label: 'Contract Status',
      values: [
        { key: 'current', label: 'Current' },
        { key: 'expiring_soon', label: 'Expiring Soon' },
        { key: 'expired', label: 'Expired' },
        { key: 'no_contracts', label: 'No Contract' },
      ],
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      hidden: isBrandUser,
      key: 'pricing_plan_id',
      label: 'Pricing Plans',
      values: sortArrayBy(Object.values(pricingPlans), 'asc', 'title').map(pricingPlan => ({
        key: pricingPlan.id,
        label: pricingPlan.title,
      })),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      hidden: isBrandUser,
      key: 'subscription_status',
      label: 'Subscription Status',
      values: [
        { key: 'active', label: 'Active' },
        { key: 'inactive', label: 'Inactive' },
      ],
    },
  ]
}

interface PageContentProps {
  filterPayload: UseFiltersPayload,
}

const PageContent = (props: PageContentProps) => {
  const { filterPayload } = props
  const { pageFilters, persistedExternalFilters, persistedFilters } = filterPayload

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const currentUserPayload = useCurrentUser()
  const { isBrandUser } = currentUserPayload

  usePricingPlans({ performHttpRequests: !isBrandUser })

  const entities = useSelector(reduxState => reduxState.entities)

  // @ts-expect-error TS2339
  const isActive = persistedExternalFilters.active || 'true'

  const agenciesPayload = useAgencies({
    filters: { ...pageFilters, active: isActive },
    performHttpRequests: true,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencies,
    filteredAgenciesCount,
    hasFilteredAgencies,
    loading,
  } = agenciesPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateAgencyModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  return (
    <>
      <Helmet>
        <title>Agencies | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <Button buttonStyle="primaryCreate" onClick={() => showCreateAgencyModal()} size="medium">
              Create
            </Button>
          </ScopedConditionalContent>
        )}
        activeTabBarItemKey={isActive}
        tabBarItems={[
          {
            icon: faCheckCircle,
            key: 'true',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ active: 'true' }),
            title: 'Active',
          },
          {
            icon: faBan,
            key: 'false',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ active: 'false' }),
            title: 'Inactive',
          },
        ]}
        title="Agencies"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredAgencies && <ListBlankState />}

            {!loading && hasFilteredAgencies && (
              <DashboardModule title="Agencies">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredAgencies.map(agency => (
                      <AgencyListItem key={agency.id} agency={agency} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredAgenciesCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              externalFilters={persistedFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(entities, currentUserPayload)}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
